import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, SxProps, Theme } from '@mui/material/styles';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PriorityHighIcon from '@mui/icons-material/PriorityHigh';
import SearchIcon from '@mui/icons-material/Search';
import { PullRequestDetails } from '@finvari/deliver';
import { PrWorkflow } from '../../workflow/prWorkflow';
import { WORKFLOW_NODES_BY_ID } from '../../workflow/workflowConfig';
import { AUTHOR_ALL } from './usePrPageUrlState';

interface PullRequestListProps {
  prs: PullRequestDetails[];
  workflowByPr: Map<number, PrWorkflow>;
  selectedPr: number | null;
  onSelectPr: (prNumber: number) => void;
  loading: boolean;
  stage: string | null; // selected workflow node, or null for every stage
  author: string;       // a GitHub login, or AUTHOR_ALL
}

// Newest-first matches how people scan the list; oldest-first is the aging view (a Kanban hygiene
// borrow) that surfaces PRs which have been sitting around.
type SortOrder = 'newest' | 'oldest';
const SORT_NEWEST: SortOrder = 'newest';
const SORT_OLDEST: SortOrder = 'oldest';

const SEARCH_DEBOUNCE_MS = 300;

// Pills that sit beside the heading, sized to stay out of the heading's way. maxWidth lets a long
// stage name or login ellipsize rather than crowd out the sort button.
const FILTER_CHIP_SX = {
  height: 22,
  maxWidth: 140,
  borderRadius: 5,
  fontSize: 12,
  '& .MuiChip-icon': { fontSize: 14 },
};

// The rows are deliberately not virtualized. The page draws from open PRs plus a capped
// recently-merged window — order 100 rows, a few thousand DOM nodes — where windowing would cost
// more than it saves (scroll jank, no browser find-in-page, no scroll-into-view for a deep-linked
// PR). What does matter at that size is style cost, so every row shares the sx objects below:
// emotion then serializes one class per rule for the whole list rather than one per row, and
// selection is a CSS state instead of a per-row style. Revisit windowing only if this ever renders
// thousands of rows.
const ROW_SX: SxProps<Theme> = {
  gap: 1.5,
  py: 1.25,
  // Transparent when unselected so the accent bar doesn't shift the row's content.
  borderLeft: '3px solid',
  borderLeftColor: 'transparent',
  // The default grey selection tint reads as "disabled" next to the blue accent bar, so tint the
  // row with the primary colour instead.
  '&.Mui-selected': {
    borderLeftColor: 'primary.main',
    bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.08),
  },
  '&.Mui-selected:hover': { bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.12) },
  // The chevron is the row's only direct-child icon — the priority icon sits inside the text block.
  '&.Mui-selected > .MuiSvgIcon-root': { color: 'primary.main' },
};

const BADGE_SX: SxProps<Theme> = {
  flexShrink: 0,
  px: 1,
  py: 0.25,
  borderRadius: 1,
  bgcolor: (theme: Theme) => alpha(theme.palette.primary.main, 0.1),
  color: 'primary.main',
  fontWeight: 700,
  fontSize: 13,
  lineHeight: 1.6,
};

const TITLE_SX: SxProps<Theme> = { display: 'flex', alignItems: 'center', gap: 0.5 };
const PRIORITY_ICON_SX: SxProps<Theme> = { fontSize: 16, flexShrink: 0 };
const TEXT_SX: SxProps<Theme> = { my: 0, minWidth: 0 };
const CHEVRON_SX: SxProps<Theme> = { flexShrink: 0, color: 'action.disabled' };

// Recent PRs read better as an age ("2 days ago"); past a week the absolute date is more useful
// than counting weeks.
const RELATIVE_CUTOFF_DAYS = 7;
const MS_PER_MINUTE = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MINUTE;
const MS_PER_DAY = 24 * MS_PER_HOUR;

function plural(n: number, unit: string): string {
  return n + ' ' + unit + (n === 1 ? '' : 's') + ' ago';
}

function formatCreated(iso?: string): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (!then) return '';
  const elapsed = Math.max(0, Date.now() - then);
  if (elapsed < MS_PER_MINUTE) return 'just now';
  if (elapsed < MS_PER_HOUR) return plural(Math.floor(elapsed / MS_PER_MINUTE), 'minute');
  if (elapsed < MS_PER_DAY) return plural(Math.floor(elapsed / MS_PER_HOUR), 'hour');
  const days = Math.floor(elapsed / MS_PER_DAY);
  if (days < RELATIVE_CUTOFF_DAYS) return plural(days, 'day');
  return new Date(then).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

const PullRequestList: React.FC<PullRequestListProps> = ({
  prs,
  workflowByPr,
  selectedPr,
  onSelectPr,
  loading,
  stage,
  author,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sortOrder, setSortOrder] = useState<SortOrder>(SORT_NEWEST);
  const [sortAnchor, setSortAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const items = useMemo(() => {
    // A "#" in the query is decoration on the PR number, not part of it.
    const needle = search.trim().toLowerCase().replace(/^#/, '');
    const matched = needle
      ? prs.filter(
          pr =>
            String(pr.pr.number).includes(needle) ||
            pr.pr.title.toLowerCase().includes(needle) ||
            (pr.pr.user?.login || '').toLowerCase().includes(needle)
        )
      : prs;

    const byDate = [...matched].sort(
      (a, b) => new Date(a.pr.created_at).getTime() - new Date(b.pr.created_at).getTime()
    );
    if (sortOrder === SORT_NEWEST) byDate.reverse();
    return byDate;
  }, [prs, search, sortOrder]);

  // Fall back to the raw id for a stage the config no longer defines (e.g. a stale bookmarked URL).
  const stageLabel = stage ? WORKFLOW_NODES_BY_ID[stage]?.label ?? stage : '';

  return (
    <Paper
      variant="outlined"
      sx={{ height: '100%', borderRadius: 2, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >
      <Box sx={{ p: 2, pb: 1.5, flexShrink: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5 }}>
          <Typography variant="h6" fontWeight={700} sx={{ flexShrink: 0 }}>Pull Requests</Typography>
          {/* What the list is narrowed to. The stage comes from the diagram and the author from the
              dropdown above it, so without these the short list looks unexplained. */}
          {stage && (
            <Chip size="small" variant="outlined" color="primary" label={stageLabel} sx={FILTER_CHIP_SX} />
          )}
          {author !== AUTHOR_ALL && (
            <Chip
              size="small"
              variant="outlined"
              icon={<PersonOutlineIcon />}
              label={author}
              sx={FILTER_CHIP_SX}
            />
          )}
          <Button
            size="small"
            variant="outlined"
            endIcon={<KeyboardArrowDownIcon />}
            onClick={e => setSortAnchor(e.currentTarget)}
            sx={{ borderRadius: 5, px: 1.5, ml: 'auto', flexShrink: 0 }}
          >
            Date
          </Button>
        </Box>
        <Menu anchorEl={sortAnchor} open={Boolean(sortAnchor)} onClose={() => setSortAnchor(null)}>
          {[
            { order: SORT_NEWEST, label: 'Newest first' },
            { order: SORT_OLDEST, label: 'Oldest first' },
          ].map(opt => (
            <MenuItem
              key={opt.order}
              selected={sortOrder === opt.order}
              onClick={() => {
                setSortOrder(opt.order);
                setSortAnchor(null);
              }}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Menu>
        <TextField
          fullWidth
          size="small"
          placeholder="Search PRs..."
          value={searchInput}
          onChange={e => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              bgcolor: 'action.hover',
              '& fieldset': { borderColor: 'transparent' },
            },
          }}
        />
      </Box>

      <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
        {loading && prs.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            No pull requests to show.
          </Typography>
        ) : (
          <List disablePadding>
            {items.map(pr => {
              const number = pr.pr.number;
              const selected = number === selectedPr;
              return (
                <ListItemButton
                  key={number}
                  selected={selected}
                  divider
                  onClick={() => onSelectPr(number)}
                  sx={ROW_SX}
                >
                  <Box sx={BADGE_SX}>#{number}</Box>
                  <ListItemText
                    primary={
                      <Box sx={TITLE_SX}>
                        {workflowByPr.get(number)?.priority && (
                          <Tooltip title="Priority">
                            <PriorityHighIcon color="error" sx={PRIORITY_ICON_SX} />
                          </Tooltip>
                        )}
                        <Typography variant="body2" fontWeight={600} noWrap>{pr.pr.title}</Typography>
                      </Box>
                    }
                    secondary={
                      <Tooltip title={pr.pr.created_at ? new Date(pr.pr.created_at).toLocaleString() : ''}>
                        <Typography variant="caption" color="text.secondary" component="span">
                          {pr.pr.user?.login || 'Unknown'} · {formatCreated(pr.pr.created_at)}
                        </Typography>
                      </Tooltip>
                    }
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    sx={TEXT_SX}
                  />
                  <ChevronRightIcon fontSize="small" sx={CHEVRON_SX} />
                </ListItemButton>
              );
            })}
          </List>
        )}
      </Box>
    </Paper>
  );
};

export default PullRequestList;
